import { defineStore } from "pinia";
import { ISanitaryProtocol } from "../../models/sanitaire/sanitaryProtocol"
import { ISanitaryProtocolFilter } from "../../models/filters/sanitaire/sanitaryProtocolFilter"
import { ISanitaryProtocolForm } from "../../models/forms/sanitaire/sanitaryProtocolForm";
import { ServiceOperationResult } from "../../models/api/serviceOperationResult";
import DataOperationError from "../../models/api/dataOperationError";
import { IDataOperationResult } from "../../models/api/dataOperationResult";
import SanitaryProtocolService from "../../services/sanitaire/SanitaryProtocolService";
import  { useBoars }  from "../boars.store";
import { ICenter } from "../../models/center";
import i18n from "@/i18n";
import { ITypeProtocol } from "../../models/sanitaire/typeProtocol";

export const useSanitaryProtocolStore = defineStore("sanitaryProtocols", {
    state: () => ({
        datas: [] as Array<ISanitaryProtocol>,
        current : {} as ISanitaryProtocol,
        itemForm : {} as ISanitaryProtocolForm,
        isLoading: false as boolean,
    }),
    getters: {
        getWithLinkedAnimals: (state) => {
            const boarStore = useBoars();
            return state.datas.map(sp => {
                return {
                    ...sp,
                    sanitaryProtocolAnimals: sp.sanitaryProtocolAnimals?.map(a => {
                        return {
                            ...a,
                            boar: boarStore.datas.find(b => b.id === a.boarId)!
                        }
                    })
                }
            })
        },
        getTypeProtocolByBoarId: (state) => {
            return (boarId: number): ITypeProtocol[] => {
                const protocols: ITypeProtocol[] = [];
                state.datas.forEach(sp => {
                    if (sp.sanitaryProtocolAnimals?.some(a => a.boarId === boarId) && !protocols.some(p => p.id === sp.typeProtocol.id)) {
                        protocols.push(sp.typeProtocol);
                    }
                });
                return protocols;
            }
        }
    },
    actions: {

        /**
         * Chargement des données dans le store
         * @param filter Le filtre à appliquer pour le chargement des données
         * @returns
         */
        async load(filter: ISanitaryProtocolFilter) {
            const boarStore = useBoars();
            this.isLoading = true;
            let result = await SanitaryProtocolService.getAll<ISanitaryProtocolFilter,ISanitaryProtocol>(filter);
            this.isLoading = false;
            this.datas = [];
            if (!result.error) {
                var resultData = result.data as ISanitaryProtocol[];
                this.datas = resultData.map(sp => {
                    return {
                        ...sp,
                        sanitaryProtocolAnimals: sp.sanitaryProtocolAnimals?.map(a => {
                            return {
                                ...a,
                                boar: boarStore.datas.find(b => b.id === a.boarId)!
                            }
                        })
                    }
                });
            }
            return result;
        },

        /**
         * Récupération d'un sanitaryProtocol par son identifiant
         * @param id
         * @returns
         */
        async get(id: number) {
            let result = await SanitaryProtocolService.getById<ISanitaryProtocol>(id);
            // mettre à jour dans le store ?
            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == id);
                if (index >= 0)
                    this.datas[index] = result.data as ISanitaryProtocol;
                // on le stock dans le current puis dans le form
                this.current = result.data as ISanitaryProtocol;
                this.itemForm = this.toForm(result.data as ISanitaryProtocol);
            }
            return result;
        },

        /**
        * Récupération de l'objet dans le store
        * @param id Identifiant de l'objet
        * @returns
        */
        async searchStoreById (id: number) {
            this.current = this.datas.find((objet) => objet.id == id)!;
            this.itemForm = await this.toForm(this.current as ISanitaryProtocol);
            return this.current;
        },

        /**
         * Mise à jour ou création d'un objet
         * @param maj
         * @returns
         */
        async save(maj:boolean = true, isPatch:boolean = false)
        {
            let result = {} as ServiceOperationResult<ISanitaryProtocol|IDataOperationResult<ISanitaryProtocol>>;
            if ( this.itemForm.id > 0) {
                result = await SanitaryProtocolService.update(this.itemForm);

                if ( !result.error)
                {
                    this.current = result.data as ISanitaryProtocol;
                    if ( maj ) {
                        let index = this.datas.findIndex((a) => a.id == this.itemForm.id);
                        if (index >= 0) {
                            this.datas[index] = this.current;
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                            return new ServiceOperationResult<IDataOperationResult<ISanitaryProtocol>>(true, {errors: error});
                        }
                    }
                }
            } else {
                result = await SanitaryProtocolService.create(this.itemForm);
                if ( !result.error)
                {
                    this.current = result.data as ISanitaryProtocol;
                    this.datas.push(this.current);
                }

            }
            return result;
        },

        /**
         * Delete de l'objet
         * @returns
         */
        async delete() {
            let result = await SanitaryProtocolService.delete<ISanitaryProtocol>(this.current.id);

            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == this.current.id);
                if (index >= 0) {
                    // On remove l'élément des données
                    this.datas.splice(index, 1);
                }

                this.current = this.newItem();
                this.itemForm = this.newForm();
            }
            return result;
        },

        /**
         * Transformation de l'objet en formulaire
         * @param item SanitaryProtocol
         * @returns
         */
        toForm(item: ISanitaryProtocol): ISanitaryProtocolForm {
            const form = {} as ISanitaryProtocolForm;
            form.id = item.id;
            form.name = item.name;
            form.typeProtocolId = item.typeProtocol.id;
            form.sanitaryProtocolAnimals = item.sanitaryProtocolAnimals?.map(a => a.boarId) || [];
            this.itemForm = form;
            return form;
        },

        /**
         * Permet d'initialiser un objet vide pour le formulaire
         * @returns
         */
        newForm(): ISanitaryProtocolForm {
            this.itemForm = {
                id: 0,
                centerId: 0,
                name: "",
                typeProtocolId: 0,
                sanitaryProtocolAnimals: [],
            } as ISanitaryProtocolForm;
            return this.itemForm;
        },

        /**
         *  Permet d'initialiser un objet vide pour les referentiels
         * @returns
         */
        newItem(): ISanitaryProtocol {
            this.current = {
                id: 0,
                center: {id:0} as ICenter,
                name: "",
                typeProtocol: { id: 0, code: "", libelle: "" },
                sanitaryProtocolAnimals: []
            } as ISanitaryProtocol;

            this.itemForm = this.toForm(this.current);
            return this.current;
        },
    }
})
